/**
 * Deep Linking Handler for Capacitor
 * Handles app links like promptnote://snippet/123 and OAuth callbacks.
 *
 * OAuth flow (PKCE):
 *   Browser OAuth (GitHub, Apple-Android) → redirects to promptnote://auth-callback?code=...
 *   → exchangeCodeForSession(code) → supabase session established → Browser.close()
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from './supabaseClient';

let deepLinkInitialized = false;

/**
 * Handle an incoming deep link URL.
 * @param {string} url
 */
const handleDeepLink = (url) => {
  // OAuth callback: promptnote://auth-callback?code=... (PKCE flow)
  if (url.startsWith('promptnote://auth-callback')) {
    console.log('[DeepLink] OAuth callback received:', url);
    try {
      const queryString = url.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const code = params.get('code');
      if (code) {
        supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
          if (error) {
            console.error('[DeepLink] Failed to exchange code for session:', error.message);
          } else {
            console.log('[DeepLink] OAuth session established');
          }
        });
        // Close the system browser that was opened for OAuth
        import('@capacitor/browser').then(({ Browser }) => Browser.close()).catch(() => {});
      } else {
        console.warn('[DeepLink] OAuth callback missing code param');
      }
    } catch (error) {
      console.error('[DeepLink] Error handling OAuth callback:', error);
    }
    return;
  }

  // Route-based deep links: promptnote://snippet/123, promptnote://note/456, etc.
  // These are handled by the native DeepLinkHandler (iOS) or MainActivity (Android)
  // which dispatch navigation events to the WebView. No JS handling needed here.
  console.log('[DeepLink] Native route handled by platform layer:', url);
};

/**
 * Initialize deep linking handler.
 * Call this early in app initialization (native only).
 */
export const initDeepLinks = async () => {
  if (!Capacitor.isNativePlatform()) return;

  if (deepLinkInitialized) return;

  try {
    // Handle app opened via deep link (cold start)
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      console.log('[DeepLink] App launched via deep link:', launchUrl.url);
      // Delay to allow app to fully initialize
      setTimeout(() => handleDeepLink(launchUrl.url), 100);
    }

    // Handle deep links when app is already running (warm start)
    // On iOS cold start, Capacitor fires BOTH getLaunchUrl() AND appUrlOpen for the same URL.
    // Skip the duplicate to prevent double-processing.
    App.addListener('appUrlOpen', (event) => {
      if (launchUrl?.url && event.url === launchUrl.url) {
        console.log('[DeepLink] Skipping appUrlOpen (already handled via getLaunchUrl):', event.url);
        return;
      }
      console.log('[DeepLink] App URL opened:', event.url);
      handleDeepLink(event.url);
    });

    deepLinkInitialized = true;
    console.log('[DeepLink] Deep links initialized');
  } catch (error) {
    console.error('[DeepLink] Error initializing deep links:', error);
  }
};
